/**
 * Credits service for handling credit-related API operations
 */

import { api } from '../utils/api-client'

// Types for API responses

export interface CreditBalance {
  workspaceId: string
  creditBalancePcu: string
  billingEnabled: boolean
}

export interface CreditTransaction {
  id: string
  workspaceId: string
  transactionType: 'CREDIT' | 'DEBIT'
  amountPcu: string
  description: string
  createdAt: string
  metadata?: Record<string, unknown>
  relatedUsageLogId?: string
}

export interface UsageLog {
  id: string
  requestId: string
  userId: string
  workspaceId: string
  providerId: string
  inputTokens: number
  outputTokens: number
  costInPcu: string
  agentId?: string
  sessionId?: string
  createdAt: string
  metadata?: Record<string, unknown>
  provider?: {
    provider: string
    providerModelId: string
    displayName: string
  }
}

export interface WorkspaceSummary {
  workspaceId: string
  totalCreditsPurchased: string
  totalCreditsUsed: string
  currentBalance: string
  billingEnabled: boolean
  totalTransactions: number
  totalUsageLogs: number
  createdAt: string
  updatedAt: string
}

export interface UsageByUser {
  userId: string
  user?: {
    name?: string
    email?: string
  }
  totalCostPcu: string
  totalInputTokens: number
  totalOutputTokens: number
  requestCount: number
  lastUsedAt: string
}

export interface UsageByModel {
  provider: string
  providerModelId: string
  displayName: string
  totalCostPcu: string
  totalInputTokens: number
  totalOutputTokens: number
  requestCount: number
  lastUsedAt: string
}

export interface UsageByProvider {
  provider: string
  totalCostPcu: string
  totalInputTokens: number
  totalOutputTokens: number
  requestCount: number
  modelCount: number
  lastUsedAt: string
}

export interface UserSettings {
  userId: string
  workspaceId: string
  monthlyUsageCapPcu?: string
  monthlyUsagePcu: string
  capPeriodStartDate?: string
  notificationThresholds?: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface CreditAnalytics {
  workspaceSummary: WorkspaceSummary
  usageByUser: UsageByUser[]
  usageByModel: UsageByModel[]
  usageByProvider: UsageByProvider[]
  transactions: CreditTransaction[]
  usageLogs: UsageLog[]
}

/**
 * Credit Service - handles all credit-related API calls
 */
export class CreditsService {
  private static baseUrl = '/v1'

  /**
   * Get credit balance for a workspace
   */
  static async getCreditBalance(workspaceId: string): Promise<CreditBalance> {
    const response = await api.get(
      `${this.baseUrl}/credits/workspaces/${workspaceId}/balance`,
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      }
    ) 
    
    return response.data
  }

  /**
   * Get workspace summary analytics
   */
  static async getWorkspaceSummary(workspaceId: string): Promise<WorkspaceSummary> {
    const response = await api.get(
      `${this.baseUrl}/analytics/workspaces/${workspaceId}/summary`,
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      }
    ) 
    
    // Transform the response to match our interface
    const serverData = response.data
    return {
      workspaceId,
      totalCreditsPurchased: '0', // Will be calculated from transactions in analytics hook
      totalCreditsUsed: serverData.summary?.totalUsagePcu?.toString() || '0',
      currentBalance: '0', // Will come from workspace balance
      billingEnabled: true,
      totalTransactions: 0, // Will be calculated from transactions in analytics hook
      totalUsageLogs: serverData.summary?.totalRequests || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  }

  /**
   * Get usage analytics by user
   */
  static async getUsageByUser(
    workspaceId: string,
    params?: {
      startDate?: string
      endDate?: string
      limit?: number
      offset?: number
    }
  ): Promise<UsageByUser[]> {
    const searchParams = new URLSearchParams()
    
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate)
    }
    
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate)
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    
    const queryString = searchParams.toString()
    const url = queryString 
      ? `${this.baseUrl}/analytics/workspaces/${workspaceId}/usage-by-user?${queryString}` 
      : `${this.baseUrl}/analytics/workspaces/${workspaceId}/usage-by-user`
    
    const response = await api.get(url, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    }) 
    
    // Transform server response to match our interface
    const serverData = response.data
    return serverData.userUsage?.map((user:any) => ({
      userId: user.userId,
      user: {
        name: undefined, // User data not included in server responseas Pro
        email: undefined
      },
      totalCostPcu: user.totalUsagePcu?.toString() || '0',
      totalInputTokens: user.totalInputTokens || 0,
      totalOutputTokens: user.totalOutputTokens || 0,
      requestCount: user.totalRequests || 0,
      lastUsedAt: new Date().toISOString() // Not available from server
    })) || []
  }

  /**
   * Get usage analytics by model
   */
  static async getUsageByModel(
    workspaceId: string,
    params?: {
      startDate?: string
      endDate?: string
      limit?: number
      offset?: number
    }
  ): Promise<UsageByModel[]> {
    const searchParams = new URLSearchParams()
    
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate)
    }
    
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate)
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    
    const queryString = searchParams.toString()
    const url = queryString 
      ? `${this.baseUrl}/analytics/workspaces/${workspaceId}/usage-by-model?${queryString}` 
      : `${this.baseUrl}/analytics/workspaces/${workspaceId}/usage-by-model`
    
    const response = await api.get(url, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    })
    
    // Transform server response to match our interface
    const serverData = response.data
    return serverData.modelUsage?.map((model:any) => ({
      provider: model.provider,
      providerModelId: model.providerModelId,
      displayName: model.displayName,
      totalCostPcu: model.totalUsagePcu?.toString() || '0',
      totalInputTokens: model.totalInputTokens || 0,
      totalOutputTokens: model.totalOutputTokens || 0,
      requestCount: model.totalRequests || 0,
      lastUsedAt: new Date().toISOString() // Not available from server
    })) || []
  }

  /**
   * Get usage analytics by provider
   */
  static async getUsageByProvider(
    workspaceId: string,
    params?: {
      startDate?: string
      endDate?: string
      limit?: number
      offset?: number
    }
  ): Promise<UsageByProvider[]> {
    const searchParams = new URLSearchParams()
    
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate)
    }
    
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate)
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString())
    }
    
    const queryString = searchParams.toString()
    const url = queryString 
      ? `${this.baseUrl}/analytics/workspaces/${workspaceId}/usage-by-provider?${queryString}` 
      : `${this.baseUrl}/analytics/workspaces/${workspaceId}/usage-by-provider`
    
    const response = await api.get(url, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    }) 
    
    // Transform server response to match our interface
    const serverData = response.data
    return serverData.providerUsage?.map((provider:any) => ({
      provider: provider.provider,
      totalCostPcu: provider.totalUsagePcu?.toString() || '0',
      totalInputTokens: provider.totalInputTokens || 0,
      totalOutputTokens: provider.totalOutputTokens || 0,
      requestCount: provider.totalRequests || 0,
      modelCount: provider.uniqueModels || 0,
      lastUsedAt: new Date().toISOString() // Not available from server
    })) || []
  }

  /**
   * Get credit transactions
   */
  static async getTransactions(
    workspaceId: string,
    params?: {
      transactionType?: 'CREDIT' | 'DEBIT'
      startDate?: string
      endDate?: string
      limit?: number
      offset?: number
    }
  ): Promise<{ transactions: CreditTransaction[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const searchParams = new URLSearchParams()
    
    if (params?.transactionType) {
      searchParams.append('transactionType', params.transactionType)
    }
    
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate)
    }
    
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate)
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString())
    }
    
    const queryString = searchParams.toString()
    const transactionsUrl = queryString 
      ? `${this.baseUrl}/transactions/workspaces/${workspaceId}?${queryString}` 
      : `${this.baseUrl}/transactions/workspaces/${workspaceId}`
    
    const response = await api.get(transactionsUrl, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    }) 
    
    return response.data
  }

  /**
   * Get usage logs
   */
  static async getUsageLogs(
    _workspaceId: string,
    params?: {
      userId?: string
      startDate?: string
      endDate?: string
      limit?: number
      offset?: number
    }
  ): Promise<{ usageLogs: UsageLog[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const searchParams = new URLSearchParams()
    
    if (params?.userId) {
      searchParams.append('userId', params.userId)
    }
    
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate)
    }
    
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate)
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString())
    }
    
    // Mock data for now since usage-logs endpoint doesn't exist yet
    return {
      usageLogs: [],
      pagination: {
        page: 1,
        limit: params?.limit || 100,
        total: 0,
        totalPages: 0
      }
    }
  }

  /**
   * Get user credit settings
   */
  static async getUserSettings(workspaceId: string, userId: string): Promise<UserSettings> {
    const response = await api.get(
      `${this.baseUrl}/workspaces/${workspaceId}/users/${userId}/settings`,
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      }
    ) 
    
    // Transform response to match our interface
    const serverData = response.data
    return {
      userId: serverData.userId || userId,
      workspaceId: serverData.workspaceId || workspaceId,
      monthlyUsageCapPcu: serverData.monthlyUsageCapPcu?.toString(),
      monthlyUsagePcu: serverData.monthlyUsagePcu?.toString() || '0',
      capPeriodStartDate: serverData.capPeriodStartDate,
      notificationThresholds: serverData.notificationThresholds,
      createdAt: serverData.createdAt || new Date().toISOString(),
      updatedAt: serverData.updatedAt || new Date().toISOString()
    }
  }

  /**
   * Set user monthly credit cap
   */
  static async setUserCap(
    workspaceId: string,
    userId: string,
    monthlyCapPcu: string
  ): Promise<UserSettings> {
    const response = await api.put(
      `${this.baseUrl}/workspaces/${workspaceId}/users/${userId}/cap`,
      {
        monthlyUsageCapPcu: parseFloat(monthlyCapPcu)
      },
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      }
    ) 
    
    // Transform response to match our interface
    const serverData = response.data
    return {
      userId: serverData.userId || userId,
      workspaceId: serverData.workspaceId || workspaceId,
      monthlyUsageCapPcu: monthlyCapPcu,
      monthlyUsagePcu: '0',
      capPeriodStartDate: new Date().toISOString(),
      notificationThresholds: undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  }

  /**
   * Reset user monthly usage
   */
  static async resetUserUsage(workspaceId: string, userId: string): Promise<UserSettings> {
    const response = await api.post(
      `${this.baseUrl}/workspaces/${workspaceId}/users/${userId}/reset-usage`,
      {},
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      }
    ) ;
    
    // Transform response to match our interface
    const serverData = response.data
    return {
      userId: serverData.userId || userId,
      workspaceId: serverData.workspaceId || workspaceId,
      monthlyUsageCapPcu: undefined,
      monthlyUsagePcu: '0',
      capPeriodStartDate: serverData.capPeriodStartDate || new Date().toISOString(),
      notificationThresholds: undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  }

  /**
   * Purchase credits for workspace
   * Creates a credit transaction to add PCU credits to workspace balance
   */
  static async purchaseCredits(
    workspaceId: string,
    amountPcu: string,
    description: string
  ): Promise<CreditTransaction> {
    const response = await api.post(
      `${this.baseUrl}/credits/workspaces/${workspaceId}/purchase`,
      {
        amountPcu: parseFloat(amountPcu),
        description
      },
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      }
    )
    
    return response.data
  }

  /**
   * Get comprehensive credit analytics for a workspace
   */
  static async getCreditAnalytics(workspaceId: string): Promise<CreditAnalytics> {
    const [
      workspaceSummary,
      usageByUser,
      usageByModel,
      usageByProvider,
      transactionsData,
      usageLogsData,
    ] = await Promise.all([
      this.getWorkspaceSummary(workspaceId),
      this.getUsageByUser(workspaceId, { limit: 50 }),
      this.getUsageByModel(workspaceId, { limit: 20 }),
      this.getUsageByProvider(workspaceId, { limit: 10 }),
      this.getTransactions(workspaceId, { limit: 50 }),
      this.getUsageLogs(workspaceId, { limit: 100 }),
    ])

    return {
      workspaceSummary,
      usageByUser,
      usageByModel,
      usageByProvider,
      transactions: transactionsData.transactions,
      usageLogs: usageLogsData.usageLogs,
    }
  }
}